import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { notice } from '@/components/pbo/notice'
import { HELPER_OPTION_TRUNCATED_MESSAGE } from '@/utils/consts'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { canSelectShipper, defaultShipper, scopedShipperCode, selectableShippers, ShipperScope, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { useCoMingleShipperScope } from '@/components/system/useCoMingleShipperScope'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
// endpoints(순수 URL 빌더)에서 직접 가져온다 — apis 를 거치면 request→okta-auth-js(node 빌드)까지 딸려 와
// CI Node 에서 테스트 모듈 로드가 깨진다(@peculiar/webcrypto CJS named export 인터롭).
import { buildErpHistoryItemsExcelUrl, buildErpHistorySummariesExcelUrl } from '@/services/inventory/stockMoveHistory/endpoints'
import { ErpHistorySummary, GetErpHistoriesReq } from '@/services/inventory/stockMoveHistory/types'
import {
  useGetErpHistoryItems,
  useGetErpHistorySummaries,
  useGetGoodsCodeOptions,
  useGetShipperOptions,
  useGetStyleNumberOptions,
} from '@/services/inventory/stockMoveHistory/useStockMoveHistoryQueries'
import { MESSAGES, REASON_DETAIL_MIN_LENGTH, STATUS_LABELS } from '@/pages/inventory/stockMoveHistory/constants'
import { formatDate8, formatDateTime14 } from '@/pages/inventory/stockMoveHistory/formatDate'
import StockMoveHistoryPageView, { type ActionCtx, type Option, type Row, type Wiring } from './handoff/page.generated'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import { usePermissionSlot } from '@/hooks/useMenuPermission'

/** 스타일넘버 후보 중복 제거 — 같은 STL_NO 는 첫 행만(모달 표는 스타일넘버 단위로 고른다). */
const uniqueByStyle = <T extends { STL_NO: string }>(rows: readonly T[]): T[] => {
  const seen = new Set<string>()
  return rows.filter((r) => (seen.has(r.STL_NO) ? false : (seen.add(r.STL_NO), true)))
}

/**
 * 서치헬퍼 모달 [조회] 적용 조건 — 뷰가 `wiring.rows.ROWS3/ROWS4` 의 filters 로 준다(모달 폼값 + `__applied`).
 *  brand = 브랜드명(BRD_NM, 두 모달 공통) · text = 두 번째 칸(스타일넘버 모달 STL_NO / 상품코드 모달 GDS_NM).
 */
type HelperQuery = { applied: boolean; brand: string; text: string }
const EMPTY_HELPER_QUERY: HelperQuery = { applied: false, brand: '', text: '' }
// 모달 칸은 텍스트 입력뿐이라 문자열만 받는다(그 밖의 값은 빈 조건).
const helperText = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')
const toHelperQuery = (filters: Record<string, unknown> | undefined, textKey: 'STL_NO' | 'GDS_NM'): HelperQuery => ({
  applied: Boolean(filters?.__applied),
  brand: helperText(filters?.BRD_NM),
  text: helperText(filters?.[textKey]),
})
const sameHelperQuery = (a: HelperQuery, b: HelperQuery): boolean => a.applied === b.applied && a.brand === b.brand && a.text === b.text

const EMPTY_REQ: GetErpHistoriesReq = {}

/**
 * API 행(camelCase) → 표 행(UPPER_SNAKE).
 *
 * ⚠ UI 가 달라지며 생긴 차이: 옛 표는 컬럼 정의가 `sprNm` 같은 API 키를 그대로 dataIndex 로 썼는데,
 *  생성 뷰의 컬럼은 화면 설계(데모)의 키 `SPR_NM`·`PLN_DATE` 를 쓴다. 변환 없이 넘기면 **표가 통째로 빈다**.
 *  원본 키도 남겨 둔다 — 배선(onPick → masterRow)은 계속 `sprNm`·`ordNo` 로 읽는다.
 */
const toViewRow = (r: Record<string, unknown>): Record<string, unknown> => {
  // 표기값 치환은 옛 표(StockMoveHistoryTables toHeadRow/toItemRow)와 **같은 규칙**이다 —
  //  상태 코드(WAIT/PROGRESS/…)는 라벨로, 날짜는 yyyyMMdd/14자리를 사람이 읽는 형식으로.
  //  옛 표가 하던 일을 뷰는 하지 않으므로(생성 뷰는 값을 그대로 그린다) 여기서 한다.
  const status = r.status
  const shown: Record<string, unknown> = {
    ...r,
    status: typeof status === 'string' && status ? (STATUS_LABELS[status] ?? status) : status,
    plnDate: formatDate8(r.plnDate),
    insDate: formatDateTime14(r.insDate),
    updDate: formatDateTime14(r.updDate),
    ...(r.actDate !== undefined ? { actDate: formatDate8(r.actDate) } : {}),
  }
  const out: Record<string, unknown> = { ...shown }
  for (const [k, v] of Object.entries(shown)) {
    out[k.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase()] = v // totalQty → TOTAL_QTY
    // 밑줄 없는 컬럼도 있다(totalQty → TOTALQTY). 한 벌만 내보내면 그 컬럼이 통째로 빈다(2026-09-20 st02 실측).
    out[k.toUpperCase()] = v
  }
  return out
}

/** 오늘(YYYY-MM-DD) — 마감일자 기본값(옛 조회영역: "마감일자 기본값 오늘~오늘"). */
const todayDash = (): string => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** 조회 실패(HTTP 4xx) 응답에서 서버 메시지를 꺼낸다 — 2.0 StandardResponse 는 meta.message 에 싣는다. */
const extractSearchErrorMessage = (error: unknown): string =>
  (error as { response?: { data?: { meta?: { message?: string }; message?: string } } })?.response?.data?.meta?.message ||
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
  MESSAGES.searchFailed

/** 'YY-MM-DD' 또는 'YYYY-MM-DD' → BE CLS_DATE 물리 포맷(yyyyMMdd). 뷰는 문자열로 보고한다. */
const toClsDateParam = (raw: unknown): string | undefined => {
  const s = String(raw ?? '').trim()
  if (!s) return undefined
  const d = s.length === 8 ? `20${s}` : s // 'YY-MM-DD'(8자) → 'YYYY-MM-DD'
  const digits = d.replace(/-/g, '')
  return digits.length === 8 ? digits : undefined
}

/**
 * 조회영역 값(키 = filterKey) → BE 조회 조건.
 * 옛 `filterToRequest.toErpHistoriesReq` 와 **같은 규칙**이되 입력 형태만 다르다 — 옛 조회영역은
 * dayjs 를 담은 antd Form 값을 줬고 생성 뷰는 문자열을 준다. 나가는 요청(GetErpHistoriesReq)은 동일하다.
 */
const toReq = (filters: Record<string, unknown>): GetErpHistoriesReq => {
  const text = (v: unknown) => { const s = String(v ?? '').trim(); return s || undefined }
  const range = filters.est03ClsDate as unknown[] | undefined
  const sprNm = text(filters.SPR_NM)
  const from = Array.isArray(range) ? toClsDateParam(range[0]) : undefined
  const to = Array.isArray(range) ? toClsDateParam(range[1]) : undefined
  const ordNo = (Array.isArray(filters.ORD_NO) ? filters.ORD_NO : []).map((v) => String(v).trim()).filter(Boolean)
  const stlNo = text(filters.STL_NO)
  const gdsCd = text(filters.GDS_CD)
  return {
    ...(sprNm ? { sprNm } : {}),
    ...(from ? { clsDateFrom: from } : {}),
    ...(to ? { clsDateTo: to } : {}),
    ...(ordNo.length ? { ordNo } : {}),
    ...(stlNo ? { stlNo } : {}),
    ...(gdsCd ? { gdsCd } : {}),
  }
}

/** 되살린 조회 조건 → 조회영역 값(키 = filterKey). 없는 값은 얹지 않는다(기본값이 살아 있게). */
const toFilterValues = (q: Record<string, unknown> | undefined): Record<string, unknown> => {
  if (!q) return {}
  const dash = (v: unknown) => { const s = String(v ?? ''); return /^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6)}` : undefined }
  const from = dash(q.clsDateFrom)
  const to = dash(q.clsDateTo)
  // 다건 키는 **1건이면 문자열**로 돌아온다(useParsedQuery 의 qs comma 규약) — 배열로 맞춘다. 안 맞추면 1건 조회가 복원에서 빠진다.
  const ordNo = (Array.isArray(q.ordNo) ? q.ordNo : q.ordNo == null ? [] : [q.ordNo]).map(String).filter(Boolean)
  return {
    ...(q.sprNm ? { SPR_NM: String(q.sprNm) } : {}),
    ...(from && to ? { est03ClsDate: [from, to] } : {}),
    ...(ordNo.length ? { ORD_NO: ordNo } : {}),
    ...(q.stlNo ? { STL_NO: String(q.stlNo) } : {}),
    ...(q.gdsCd ? { GDS_CD: String(q.gdsCd) } : {}),
  }
}

/**
 * EST05 재고이동내역조회 — 1.0 EST03 을 신규 디자인(pbo-core)으로 재구현한 화면. (LOGISPRD-10669)
 *
 * 겉(조회영역·위아래 표·엑셀 사유 모달·서치헬퍼)은 컨덕터 뷰(`./handoff/page.generated`)가 그리고,
 * 이 파일은 **옛 훅·API·세션/URL 유지를 그대로 두고** 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드).
 * 옛 화면 컴포넌트(`components/StockMoveHistory*`)가 나눠 갖던 상태를 여기로 모았다 — 선택·검색어·페이저는 뷰가 가진다.
 *
 * HEAD(문서 헤더)·ITEM(문서 상세)을 같은 필터로 조회하고, HEAD 행 선택으로 ITEM 을 거른다(마스터-디테일).
 *
 * 엑셀 사유 처리: 서버 xlsx 요청에 `reasonCode`·`detailedReason` 을 실어 서버가 EXPORT 감사를 1행 남긴다
 * (LOGISPRD-15563 BE · LOGISPRD-15597 FE). FE 공용 로그 API 는 부르지 않는다.
 *
 * ── UI 가 달라지며 생긴 차이(개발된 동작이 그대로 돌게 맞춰 둔 지점) ──
 * · 조회영역 값이 dayjs 가 아니라 **문자열**로 온다 — 옛 `toErpHistoriesReq` 대신 위 `toReq` 가 같은 요청을 만든다.
 * · 서치헬퍼(스타일넘버·상품코드)는 뷰가 모달까지 그린다 — 고른 값은 뷰가 그 인풋에 넣는다. 배선은 후보 목록만 준다
 *   (모달 [조회] 적용 조건 → 옛 훅, 아래 syncStlQuery·syncGdsQuery).
 * · 확정취소는 이번 범위 밖(조회 전용) — 뷰에도 버튼이 없다.
 */
function StockMoveHistory({ coMingle = false }: Readonly<{ coMingle?: boolean }>) {
  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()
  // 기본 화주 = 현재 세션 화주(shipper_code — 화주 전환을 반영한다, LOGISPRD-14651) — 옛 조회영역과 같다(StockMoveHistoryFilter: "기본값 = 프로필 화주사").
  //  마운트 스냅샷이라 useRef — 세션은 화면이 떠 있는 동안 바뀌지 않는다.
  const sessionShipperRef = useRef<string>()
  const canEditShipperRef = useRef<boolean>()
  const shipperNameRef = useRef<string>()
  const scopeRef = useRef<ShipperScope>()
  if (sessionShipperRef.current === undefined) {
    const u = AuthStorage.user.get()
    // 재고통합 ON 이면 잠긴 29CM·INOOINT 가 MUSINSA 도 고른다(LOGISPRD-15610).
    const scope = withCoMingle(shipperScopeOf(u), coMingle)
    scopeRef.current = scope
    sessionShipperRef.current = scope.shipperCode
    shipperNameRef.current = String((u as { shipperName?: string }).shipperName ?? '')
    // 통합 운영 화주(FREE_SHIPPERS 4종)만 화주를 바꿀 수 있다 — 그 외는 자기 화주 고정(1.0 FilterBar 동치, 2.0 공용 규칙 LOGISPRD-14652).
    canEditShipperRef.current = !scope.locked
  }
  const scope = scopeRef.current as ShipperScope

  /**
   * 남겨 둔 조회 조건(URL) — 조회영역 초기값(filterInitial)과 진입 조회가 같은 값을 쓴다. 비면 첫 진입이다.
   *  화주 고정 계정이면 URL 의 화주를 자기 화주로 덮는다(권한 밖 조회를 URL 로 뚫지 못하게).
   *  마운트 스냅샷이라 useState 초기값으로 한 번만 읽는다.
   */
  const [restored] = useState(() => {
    const r = toFilterValues(parsedQs as Record<string, unknown>)
    if (!Object.keys(r).length) return undefined
    if (!canEditShipperRef.current && sessionShipperRef.current) r.SPR_NM = scopedShipperCode(scope, String(r.SPR_NM ?? ''))
    return r
  })
  /*
   * 남겨 둔 조건이 [조회] 필수 검증(화주·마감일자)을 넘으면 그 조건으로 시작한다 — 아래 진입 조회가 이 키로 refetch 한다(LOGISPRD-14439).
   *  첫 진입엔 조회하지 않는다 — 조회 버튼으로만 실행한다(ST02·ST10·ST11 동일. ITEM 무필터 조회는 BE 가 400 으로 막는다).
   */
  const [queryParams, setQueryParams] = useState<GetErpHistoriesReq>(() => {
    const req = restored ? toReq(restored) : EMPTY_REQ
    return req.sprNm && req.clsDateFrom && req.clsDateTo ? req : EMPTY_REQ
  })
  const [masterRow, setMasterRow] = useState<ErpHistorySummary | null>(null)

  const { items: summaries, isFetching: isHeadFetching, refetch: refetchSummaries } = useGetErpHistorySummaries(queryParams)

  /**
   * 상세는 조회 버튼이 아니라 **선택한 문서** 기준으로 받아 온다 — 최초 조회 때는 HEAD 만 채우고,
   * HEAD 행을 골라야 그 문서의 상세가 조회된다(1.0 EST03 행 더블클릭 흐름 · 리뷰 피드백 2026-08-06).
   */
  const itemParams = useMemo<GetErpHistoriesReq>(
    () => (masterRow ? { ...(masterRow.sprNm ? { sprNm: masterRow.sprNm } : {}), ordNo: [masterRow.ordNo] } : EMPTY_REQ),
    [masterRow],
  )
  const { items, isFetching: isItemFetching } = useGetErpHistoryItems(itemParams)
  const { isLoading: isDownloading, onDownloadExcel } = useExcelDownload({ fileNamePrefix: '재고이동내역' })
  const { options: shipperOptions } = useGetShipperOptions()
  /**
   * 서치헬퍼(스타일넘버·상품코드) 후보 — 옛 훅 그대로. 조회 조건의 화주로 거른다.
   *
   * 후보는 **모달이 열려 있고 모달 안 [조회](또는 입력칸 엔터)를 눌렀을 때만** 받는다 — 옛 StockMoveHistoryFilter 의
   *  `isStyleHelperOpen && styleHelperSearched`·1.0 헬퍼와 같다(2026-08-04 피드백 "모달을 열자마자 조회하지 않는다").
   *  열림 게이트(wiring.onModal, #834)는 그대로 둔다 — 그게 없으면 화주만으로 진입 즉시 후보 API(무페이징 distinct)가 나가고,
   *  그 API 가 500 이면 도우미를 열지도 않았는데 전역 알럿이 뜬다(디자인 QA 2026-09-29).
   *  [조회] 신호는 뷰가 준다: [조회]·엔터 때 그 시점의 모달 값을 `__applied` 와 함께 `wiring.rows.ROWS3/ROWS4` 의 filters 로 넘기고,
   *  열 때·[초기화] 때는 `{}` 를 넘긴다. 입력만 하고 누르지 않으면 받지 않는다.
   */
  const [helperOpen, setHelperOpen] = useState({ stl: false, gds: false })
  const [stlQuery, setStlQuery] = useState<HelperQuery>(EMPTY_HELPER_QUERY)
  const [gdsQuery, setGdsQuery] = useState<HelperQuery>(EMPTY_HELPER_QUERY)
  const stlQueryRef = useRef(stlQuery)
  const gdsQueryRef = useRef(gdsQuery)
  // 뷰가 넘긴 적용 조건 객체 — [조회]를 누를 때마다 새 객체라, 같은 값으로 다시 눌러도 "또 눌렀음" 을 알 수 있다(안내 알럿용).
  const gdsAppliedObjRef = useRef<unknown>()
  /** 스타일넘버 모달 적용 조건(브랜드명·스타일넘버)을 상태로 올린다 — 바뀔 때만(렌더 중 setState 를 피해 마이크로태스크로). */
  const syncStlQuery = useCallback((filters?: Record<string, unknown>) => {
    const next = toHelperQuery(filters, 'STL_NO')
    if (sameHelperQuery(next, stlQueryRef.current)) return
    stlQueryRef.current = next
    queueMicrotask(() => setStlQuery(next))
  }, [])
  /** 상품코드 모달 적용 조건(브랜드명·상품명)을 상태로 올린다 — 조건 없이 [조회]하면 받지 않으므로(아래 enabled) 안내한다. */
  const syncGdsQuery = useCallback((filters?: Record<string, unknown>) => {
    const next = toHelperQuery(filters, 'GDS_NM')
    const pressed = filters !== gdsAppliedObjRef.current && next.applied
    gdsAppliedObjRef.current = filters
    // 말없이 0건이면 고장처럼 보인다 — 조건 없이 누를 때마다 알린다(같은 값으로 다시 눌러도 다시 알린다).
    if (pressed && !next.brand && !next.text) queueMicrotask(() => notice.invalid(MESSAGES.goodsHelperConditionRequired))
    if (sameHelperQuery(next, gdsQueryRef.current)) return
    gdsQueryRef.current = next
    queueMicrotask(() => setGdsQuery(next))
  }, [])
  //  화주 고정 계정은 **항상 고를 수 있는 화주**(자기 화주, 재고통합 ON 이면 MUSINSA — LOGISPRD-15610)로 후보를 받는다 — URL 복원값이 남의 화주여도 그쪽 후보가 보이면 안 된다
  //  (search·filterInitial 과 같은 잠금. 세 곳 중 하나라도 빠지면 권한 밖 데이터가 샌다).
  //  화주전환 가능 계정은 조회한 화주 → URL 복원 화주 → 세션 화주(조회영역 기본값과 같은 폴백) 순 — 폴백이 없으면 조회 전에 도우미를 열었을 때
  //  후보가 비어 화주 고정 계정과 동작이 갈린다(#834 리뷰). 조회영역에서 화주를 바꾸고 [조회] 전에 열면 세션 화주 기준 후보가 뜬다(조회 조건 미확정 상태, 1.0 과 같다).
  const helperShipper = canEditShipperRef.current
    ? String(queryParams.sprNm ?? restored?.SPR_NM ?? sessionShipperRef.current ?? '')
    : scopedShipperCode(scope, String(queryParams.sprNm ?? restored?.SPR_NM ?? ''))
  /*
   * 스타일넘버 — 2.0 style-numbers 는 styleNumber 가 정확일치라 브랜드명(부분일치)만 서버로 보내고,
   *  모달의 스타일넘버 칸은 받은 후보를 화면에서 부분일치(대소문자 무시)로 거른다.
   *  조건 없이 [조회]해도 받는다(옛 모달·1.0 헬퍼와 같다) — 대신 BE 가 1,000행에서 자르므로 상한에 닿으면 조건을 좁히라고 알린다.
   */
  const styleHelperEnabled = !!helperShipper && helperOpen.stl && stlQuery.applied
  const styleParams = useMemo(() => ({ shipperCode: helperShipper, ...(stlQuery.brand ? { brandName: stlQuery.brand } : {}) }), [helperShipper, stlQuery.brand])
  const {
    items: styleItems,
    isFetching: isStyleFetching,
    isTruncated: isStyleTruncated,
    dataUpdatedAt: styleUpdatedAt,
  } = useGetStyleNumberOptions(styleParams, styleHelperEnabled)
  // 2.0 헬퍼는 1,000행에서 자른다 — 모달 [조회]로 받은 결과가 상한에 닿았을 때 알린다.
  //  게이트를 함께 보는 이유: 모달을 닫으면 적용 조건이 비워져 queryKey 가 캐시된 '조건 없음' 키로 돌아가는데,
  //  v5 는 disabled 여도 캐시 data 를 돌려줘 닫힌 화면에 안내가 뜬다. 다시 열고 [조회]하면 게이트가 켜져 다시 알린다.
  useEffect(() => {
    if (styleHelperEnabled && isStyleTruncated) notice.failure(HELPER_OPTION_TRUNCATED_MESSAGE)
  }, [styleHelperEnabled, isStyleTruncated, styleUpdatedAt])
  const styleOptions = useMemo(() => {
    const kw = stlQuery.text.toLowerCase()
    return kw ? styleItems.filter((o) => String(o.STL_NO ?? '').toLowerCase().includes(kw)) : styleItems
  }, [styleItems, stlQuery.text])
  /*
   * 상품코드 — goods-codes 는 무페이징 distinct 라 대형 화주를 조건 없이 받으면 응답이 수십 MB 가 되어 브라우저가 멎는다
   *  (재고현황·재고현황(단가) 주석 · dev 실기동 확인). 화주는 늘 있으므로 **브랜드명·상품명 중 하나**를 필수로 둔다(없으면 syncGdsQuery 가 안내).
   *  두 조건 모두 API 가 받는다(brandName·itemName) — 화면에서 다시 거르지 않는다.
   */
  const goodsParams = useMemo(
    () => ({ shipperCode: helperShipper, ...(gdsQuery.brand ? { brandName: gdsQuery.brand } : {}), ...(gdsQuery.text ? { itemName: gdsQuery.text } : {}) }),
    [helperShipper, gdsQuery.brand, gdsQuery.text],
  )
  const { items: goodsOptions, isFetching: isGoodsFetching } = useGetGoodsCodeOptions(
    goodsParams,
    !!helperShipper && helperOpen.gds && gdsQuery.applied && (!!gdsQuery.brand || !!gdsQuery.text),
  )

  /*
   * 진입 조회 — 남겨 둔 조건으로 돌아오면(탭을 다녀옴) 그 조건으로 한 번 조회한다. 조회영역만 되살고 표가 비던
   *  문제(LOGISPRD-14439). 화주로 가른다 — [조회] 필수 검증을 넘은 조건만 queryParams 로 시작한다(위 초기값).
   */
  const didRestoreSearch = useRef(false)
  useEffect(() => {
    if (didRestoreSearch.current) return
    didRestoreSearch.current = true
    if (!queryParams.sprNm) return
    refetchSummaries()
      .then(({ error }) => { if (error) notice.failure(extractSearchErrorMessage(error)) })
      .catch((e: unknown) => notice.failure(extractSearchErrorMessage(e)))
  }, [queryParams, refetchSummaries])

  /** 엑셀 — 사유를 실어 서버 xlsx 를 받는다(서버가 사유를 감사에 남긴다). */
  // async 다 — 생성본은 `Promise.resolve(슬롯(...)).then(성공, 실패)` 로 부른다. 동기 throw 면
  //  Promise 로 감싸기 전에 터져 모달 밖으로 예외가 새어 나간다(실패해도 모달은 열린 채 남아야 한다).
  const runDownload = useCallback(
    async (ctx: ActionCtx, target: 'head' | 'item'): Promise<string | false> => {
      const reason = typeof ctx.form?.reason === 'string' ? ctx.form.reason : ''
      const detail = typeof ctx.form?.detail === 'string' ? ctx.form.detail.trim() : ''
      // 옛 모달은 [확인] 을 잠가 지키던 선이다(REASON_DETAIL_MIN_LENGTH). 생성 뷰는 안내만 띄우고
      //  입력을 막지 않으므로(길이 검증 슬롯 없음) 여기서 같은 선을 지킨다 — 빠지면 1글자 사유가 감사로그에 남는다.
      if (!reason || detail.length < REASON_DETAIL_MIN_LENGTH) {
        notice.invalid(`사유코드를 고르고 상세사유를 ${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해 주세요.`)
        throw new Error('사유 미입력')
      }
      // 사유는 서버가 EXPORT 감사에 `{코드}_{상세}` 로 남긴다 — 나눠 보낸다(LOGISPRD-15597).
      const downloadReason = { reasonCode: reason, detailedReason: detail }
      // 화면에 떠 있는 결과와 같은 조건으로 받아야 한다 — HEAD 는 마지막 조회 조건, ITEM 은 선택 문서 기준이다.
      onDownloadExcel(
        target === 'head'
          ? buildErpHistorySummariesExcelUrl(queryParams, downloadReason)
          : buildErpHistoryItemsExcelUrl(itemParams, downloadReason),
      )
      return false // 토스트는 다운로드 훅이 낸다
    },
    [onDownloadExcel, queryParams, itemParams],
  )

  // ── 뷰 배선(Wiring) — 배선 객체는 매 렌더 새로 만든다(문서 51). ──
  // 엑셀 사유코드 콤보 — 옛 모달이 쓰던 공용 콤보 그대로(코드/명 배열이 짝을 이룬다. 명이 비면 코드로 대체).
  const { data: exportCombo } = useGetExportCombo({ errorToast: true })
  const reasonOptions = useMemo(
    () =>
      (exportCombo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code: string, idx: number) => ({
        value: code,
        label: exportCombo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [exportCombo],
  )

  const wiring: Wiring = {
    // 화면관리 그리드 설정(LOGISPRD-17251) — 메인 표만 그 그리드 코드를 따른다. 모달 안 표는 메뉴 단위 값(docs/conventions/grid-code.md).
    permission: usePermissionSlot('est05', { est03HeadTable: 'EST05_HEAD', est03ItemTable: 'EST05_ITEM' }),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { est03HeadTable: coreTableLayoutSlot('EST05_HEAD'), est03ItemTable: coreTableLayoutSlot('EST05_ITEM') },
    rows: {
      /*
       * 행 key = **BE 행 id**. 뷰는 안 실으면 배열 인덱스를 key 로 쓰는데(생성본 TODO), 그러면 재조회·
       *  페이지 이동 뒤 같은 인덱스의 다른 행에 선택이 옮겨 붙는다. 실 id 를 실어 그 자리를 막는다.
       */
      ROWS: () => ({ rows: summaries.map((r) => ({ key: r.id, ...toViewRow(r as unknown as Record<string, unknown>) })) as unknown as Row[], loading: isHeadFetching }),
      ROWS2: () => ({ rows: items.map((r) => ({ key: r.id, ...toViewRow(r as unknown as Record<string, unknown>) })) as unknown as Row[], loading: isItemFetching }),
      /*
       * 서치헬퍼 후보 — 응답 키를 **모달 표 컬럼 키로 바꿔** 넘긴다(모달 표는 뷰가 그린다, est04 와 같은 규칙):
       *  스타일넘버는 이미 대문자(STL_NO·BRD_NM·GDS_CD — 상품명 GDS_NM 은 API 에 없어 빈 칸), 상품코드는 itemCode/brandName/itemName 이라
       *  그대로 넘기면 표가 통째로 비고 [선택]이 빈 상품코드를 조회영역에 넣는다(#834 리뷰 Critical). 바코드 칸은 대응 값이 없다.
       * 모달 [조회]를 누른 뒤에야 후보를 받으므로 응답 전엔 스피너를 — 배열만 주면 뷰가 loading 을 못 그려 "0건 · 데이터 없음" 이 먼저 보인다. total 은 빼서 서버 페이징으로 보지 않게.
       */
      // 스타일넘버 후보는 스타일 하나에 상품코드가 여럿 올 수 있는데 표에 보이는 칸은 브랜드명·스타일넘버뿐이다 — 그대로 넘기면 똑같은 행이 N개 보이고
      //  key 도 겹쳐 한 행을 고르면 전부 체크된다(#834 리뷰). 스타일넘버 기준으로 한 행만 남긴다(고른 값도 STL_NO 뿐이라 잃는 정보가 없다).
      //  q.filters = 모달 [조회] 적용 조건(뷰가 [조회]·엔터 때 잡는다) — 후보 조회의 게이트·조건이다(위 syncStlQuery·syncGdsQuery).
      ROWS3: (_demo, q) => {
        syncStlQuery(q?.filters)
        return { rows: uniqueByStyle(styleOptions).map((o) => ({ key: o.STL_NO, ...o })) as unknown as Row[], loading: isStyleFetching }
      },
      ROWS4: (_demo, q) => {
        syncGdsQuery(q?.filters)
        return {
          rows: goodsOptions.map((o) => ({ key: o.itemCode, ...o, BRD_NM: o.brandName ?? '', GDS_CD: o.itemCode ?? '', GDS_NM: o.itemName ?? '' })) as unknown as Row[],
          loading: isGoodsFetching,
        }
      },
    },
    actions: {
      /** [조회] — 옛 handleSearch 그대로: 필수 검증 → 세션·URL 저장 → 상세 비우기 → 재조회. */
      search: async (filters) => {
        // 화주 고정 계정은 조회 조건의 화주도 자기 화주로 강제한다 — 조회영역이 잠기지 않는 구조라(뷰에 disabled 슬롯 없음)
        //  여기서 한 번 더 막는다. 옛 화면은 셀렉트 자체가 disabled 라 다른 값이 올 수 없었다.
        const sprNm = canEditShipperRef.current
          ? String(filters.SPR_NM ?? '').trim()
          : scopedShipperCode(scope, String(filters.SPR_NM ?? '').trim())
        // 검증 실패 = false(조회 안 함) — 뷰는 false 일 때만 페이저·선택·마스터 pick 리셋을 건너뛴다(가이드 52 규칙 10, 재핸드오프 사전 리뷰 2026-09-29).
        if (!sprNm) {
          notice.invalid(MESSAGES.shipperRequired)
          return false
        }
        const range = filters.est03ClsDate as unknown[] | undefined
        const [from, to] = Array.isArray(range) ? [toClsDateParam(range[0]), toClsDateParam(range[1])] : [undefined, undefined]
        if (!from || !to) {
          notice.invalid(MESSAGES.clsDateRequired)
          return false
        }

        const req = toReq({ ...filters, SPR_NM: sprNm })
        // 조회한 조건만 남긴다 — 입력 중인 값이 아니라 실제로 조회에 쓴 값이 복원 대상이다.
        MenuStorage.menuOnSession.setSearch(req as Record<string, unknown>)
        menuSession.reload()

        // 새 조회 결과에는 이전 문서 선택이 유효하지 않다 — 상세를 비우고 다시 고르게 한다.
        setMasterRow(null)
        // queryParams 커밋 전에 refetch 하면 이전 조건으로 조회된다 — flushSync 로 먼저 커밋한다(클릭 문맥).
        flushSync(() => setQueryParams(req))
        const { error } = await refetchSummaries()
        replaceQuery(req as Record<string, unknown>)
        // 서버 오류 메시지는 알럿으로 표시한다(1.0 공통) — 실패 시 items 는 빈 배열이 된다.
        if (error) notice.failure(extractSearchErrorMessage(error))
      },
      est03HeadDownloadModal_ok: (ctx) => runDownload(ctx, 'head'),
      est03ItemDownloadModal_ok: (ctx) => runDownload(ctx, 'item'),
      // 서치헬퍼 [확인] — 고른 값을 인풋에 넣는 일은 뷰가 한다. 배선이 더 할 일은 없다.
      est03StlSearchModal_ok: () => false,
      est03GdsSearchModal_ok: () => false,
    },
    /** 상단에서 **행을 클릭해** 고른 문서로 하단 상세를 읽는다(뷰의 하단 표도 같은 pick 으로 걸린다). */
    onPick: (rows) => {
      const r = rows[0] as unknown as ErpHistorySummary | undefined
      setMasterRow(r ?? null)
    },
    options: {
      /**
       * 엑셀 사유코드(HEAD·ITEM 두 모달) — 1.0 공용 콤보(DOWNLOAD_CD).
       * 데모 옵션(업무활용·고객요청…)은 화면 설계용 예시라 실제 감사로그 코드와 다르다.
       * 콤보가 비면(로딩·실패) 빈 목록을 준다 — 없는 코드를 고르게 두는 쪽이 더 나쁘다.
       */
      REASONRADIO_OPTIONS: () => reasonOptions as Option[],
      REASONRADIO_OPTIONS2: () => reasonOptions as Option[],
      /**
       * 화주 목록 — **통합 운영 계정만 전체**를 본다. 그 외 계정은 자기 화주 하나만 남겨 고정한다
       * (옛 StockMoveHistoryFilter: `disabled={!canEditShipper}`).
       *
       * ⚠ UI 가 달라지며 생긴 차이: 생성 뷰의 조회 필드에는 "잠금(disabled)" 슬롯이 없어, 셀렉트 자체를
       *  비활성으로 만들지는 못한다. 대신 **고를 수 있는 값을 자기 화주 하나로 줄여** 같은 결과를 만든다
       *  (열어도 다른 화주가 없다). 조회 조건으로 나가는 값도 그 화주뿐이라 권한 밖 데이터는 조회되지 않는다.
       *  TODO(dev): 셀렉트를 실제로 잠그려면 컨덕터 조회 필드에 disabled 속성이 필요하다(컨덕터 후속).
       */
      EST03SHIPPER_OPTIONS: () => {
        const mine = sessionShipperRef.current ?? ''
        // 로그인 화주가 get-shippers 목록에 없어도(내부 운영 계정 등) 기본값이 보이도록 옵션을 보강한다(옛 shipperSelectOptions).
        if (canEditShipperRef.current) {
          return (mine && !shipperOptions.some((o) => String(o.value) === mine)
            ? [{ value: mine, label: shipperNameRef.current || mine }, ...shipperOptions]
            : shipperOptions) as Option[]
        }
        // 재고통합 ON 의 29CM·INOOINT 는 자기 화주와 MUSINSA 둘이다(LOGISPRD-15610)
        return selectableShippers(scope).map((s) =>
          shipperOptions.find((o) => String(o.value) === s.shipperCode)
            ?? { value: s.shipperCode, label: (s.shipperCode === mine ? shipperNameRef.current : s.shipperName) || s.shipperCode },
        ) as Option[]
      },
    },
    // 조회영역 기본값 — 화주는 기본 화주([defaultShipper], 재고통합 대상 계정은 MUSINSA, LOGISPRD-15719)로 덮는다(안 넘기면 뷰가 데모 리터럴을 기본값으로 쓴다).
    //  [초기화] 도 이 값으로 돌아간다(옛 화면과 같다).
    //  마감일자 기본값은 오늘~오늘이다(옛 조회영역과 같다) — 데모 기본값에는 날짜가 없어 여기서 얹지 않으면
    //  필수 필드가 빈 채로 열리고 [초기화] 도 빈 값으로 돌아간다.
    /**
     * 화주 고정 계정은 화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 보이는 것만 잠그므로 값은 기본값·search 가 따로 고정한다.
     *  재고통합 ON 의 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고르므로 잠그지 않는다(LOGISPRD-15610)
     */
    filterDisabled: canSelectShipper(scope) ? undefined : { SPR_NM: true },
    filterDefaults: {
      est03FilterCard: (demo) => ({
        ...demo,
        SPR_NM: defaultShipper(scope).shipperCode || undefined,
        est03ClsDate: [todayDash(), todayDash()],
      }),
    },
    /**
     * 진입 초기값 = 기본값 + 남겨 둔 조회 조건(위 restored). 단 **화주 고정 계정이면 복원값이 화주를 덮지 못한다** —
     * URL 에 남의 화주가 적혀 들어와도 자기 화주로 연다(권한 밖 조회를 URL 로 뚫지 못하게).
     */
    filterInitial: {
      est03FilterCard: (defaults) => ({ ...defaults, ...restored }),
    },
    useBusy: () => isDownloading,
    /** 서치헬퍼 모달 열림 — 후보 조회를 이 신호로 게이트한다(옛 isStyleHelperOpen·isGoodsHelperOpen 자리). */
    onModal: (open) => {
      setHelperOpen((s) =>
        s.stl === open.est03StlSearchModal && s.gds === open.est03GdsSearchModal ? s : { stl: open.est03StlSearchModal, gds: open.est03GdsSearchModal },
      )
      // 닫히면 그 모달의 적용 조건을 비운다 — 남겨 두면 다시 열 때 뷰가 빈 조건을 주기 전 한 렌더 동안
      //  '열림 + 지난 [조회] 조건'이 되어 후보 요청이 한 번 나간다(#861 리뷰).
      if (!open.est03StlSearchModal && stlQueryRef.current.applied) { stlQueryRef.current = EMPTY_HELPER_QUERY; setStlQuery(EMPTY_HELPER_QUERY) }
      if (!open.est03GdsSearchModal && gdsQueryRef.current.applied) { gdsQueryRef.current = EMPTY_HELPER_QUERY; setGdsQuery(EMPTY_HELPER_QUERY) }
    },
    opens: {
      // 화면에 뜬 결과가 없으면 사유 모달을 열지 않는다(옛 handleDownloadClick 의 excelNoRows 검증).
      hdExcelButton: () => {
        if (summaries.length === 0) { notice.invalid(MESSAGES.excelNoRows); return false }
        return true
      },
      itExcelButton: () => {
        if (items.length === 0) { notice.invalid(MESSAGES.excelNoRows); return false }
        return true
      },
    },
    /** [초기화] — 남겨 둔 조회 조건도 함께 지운다(조회는 트리거하지 않는다. EST04 동일). */
    onReset: () => {
      MenuStorage.menuOnSession.setSearch({})
      // 탭바는 reload 로만 새 조건을 읽는다 — 빼면 탭 링크에 지운 조건이 남아 복귀 시 진입 조회가 그 조건으로 돈다.
      menuSession.reload()
      replaceQuery({})
    },
  }

  // 타이틀 행은 뷰가 그린다(그룹 cmp.TitleRow) — 여기서 또 그리면 제목이 두 번 선다.
  return <StockMoveHistoryPageView wiring={wiring} />
}

/** 화면은 화주 범위를 첫 렌더에 고정하므로 재고통합 FF 값이 정해진 뒤에 그린다(LOGISPRD-15610) */
function StockMoveHistoryEntry() {
  const { ready, coMingle } = useCoMingleShipperScope()
  return ready ? <StockMoveHistory coMingle={coMingle} /> : <div role="status">재고이동이력을 준비하고 있어요.</div>
}

export default StockMoveHistoryEntry
